import React, { useEffect, useState, useMemo } from 'react';

const CATEGORY_LABELS = {
  identity: 'Identity',
  collaboration: 'Collaboration',
  securityAndCompliance: 'Security & Compliance',
  deviceManagement: 'Device Management',
  analyticsAndReporting: 'Analytics & Reporting',
  other: 'Other',
};

function getCategoryLabel(cat) {
  return CATEGORY_LABELS[cat] || cat;
}

const EntraRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [privilegedFilter, setPrivilegedFilter] = useState('all');

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(process.env.PUBLIC_URL + '/Entra-Roles.json');
        if (response.ok) {
          const data = await response.json();
          const sorted = [...data].sort((a, b) =>
            new Date(b.dateDetected) - new Date(a.dateDetected) ||
            a.displayName.localeCompare(b.displayName)
          );
          setRoles(sorted);
        } else {
          setError('Failed to load Entra roles data.');
        }
      } catch (err) {
        setError('Unable to load Entra roles data. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, []);

  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      if (privilegedFilter === 'privileged' && !role.isPrivileged) return false;
      if (privilegedFilter === 'standard' && role.isPrivileged) return false;

      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      return (
        role.displayName.toLowerCase().includes(search) ||
        role.description.toLowerCase().includes(search) ||
        (role.categories || '').toLowerCase().includes(search)
      );
    });
  }, [roles, searchTerm, privilegedFilter]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const sevenDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isNew = (dateDetected) => new Date(dateDetected) >= sevenDaysAgo;

  return (
    <div>
      <div className="ed-table-container">
        <h2 className="ed-changes-title">Microsoft Entra Roles</h2>
        <p className="st-section-description">
          Tracked Microsoft Entra built-in roles, including newly detected roles and their privilege level.
        </p>

        {loading && (
          <div className="ed-loading">
            <div className="ed-loading-spinner"></div>
            <span>Loading Entra roles...</span>
          </div>
        )}

        {error && (
          <div className="ed-error">
            <div className="ed-error-icon">⚠️</div>
            <div className="ed-error-content">
              <h3>Unable to Load Roles</h3>
              <p>{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="st-filters">
              <div className="ed-search-wrapper">
                <svg className="ed-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                </svg>
                <input
                  type="text"
                  className="ed-search-input"
                  placeholder="Search by role name, description, or category..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button
                    className="ed-search-clear"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="st-filter-group">
                <label className="st-filter-label">Privilege:</label>
                <div className="st-filter-buttons">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'privileged', label: 'Privileged' },
                    { value: 'standard', label: 'Standard' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      className={`st-filter-btn${privilegedFilter === opt.value ? ' active' : ''}`}
                      onClick={() => setPrivilegedFilter(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ed-search-results">
                Showing {filteredRoles.length} of {roles.length} roles
              </div>
            </div>

            {filteredRoles.length === 0 ? (
              <div className="ed-no-results">
                <h3>No roles found</h3>
                <p>Try adjusting your search or filters.</p>
              </div>
            ) : (
              <table className="ed-table st-roles-table">
                <thead>
                  <tr>
                    <th>Role Name</th>
                    <th>Description</th>
                    <th>Categories</th>
                    <th>Privileged</th>
                    <th>Date Detected</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.map(role => (
                    <tr key={role.id} className={isNew(role.dateDetected) ? 'st-row-new' : ''}>
                      <td data-label="Role Name">
                        <span className="st-role-name">{role.displayName}</span>
                        {isNew(role.dateDetected) && (
                          <span className="st-badge st-badge-new">New</span>
                        )}
                      </td>
                      <td data-label="Description">{role.description}</td>
                      <td data-label="Categories">
                        <div className="st-categories">
                          {(role.categories || '').split(',').filter(c => c.trim()).map(cat => (
                            <span key={cat.trim()} className="st-category-badge">
                              {getCategoryLabel(cat.trim())}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td data-label="Privileged">
                        {role.isPrivileged ? (
                          <span className="st-badge st-badge-privileged">Privileged</span>
                        ) : (
                          <span className="st-badge st-badge-standard">Standard</span>
                        )}
                      </td>
                      <td data-label="Date Detected">
                        {new Date(role.dateDetected).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EntraRoles;
